import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Award, 
  Medal, 
  Trophy,
  Users,
  Mail,
  FileCheck,
  Calendar,
  Building2,
  ArrowRight,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Clock,
  TrendingUp,
  TrendingDown,
  Send,
  CheckCircle,
  AlertCircle,
  CalendarDays,
  XCircle,
  Target,
  Beaker,
  Sparkles,
  ShieldAlert,
  UserX,
  Zap,
  BarChart3,
  Bell
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  Gold: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-200", chart: "#FFD700" },
  Silver: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200", chart: "#C0C0C0" },
  Bronze: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-200", chart: "#CD7F32" },
  Unranked: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-100", chart: "#E2E8F0" }
};

const TierIcon = ({ tier, className = "h-5 w-5" }) => {
  switch (tier) {
    case "Gold": return <Trophy className={`${className} text-yellow-500`} />;
    case "Silver": return <Medal className={`${className} text-slate-400`} />;
    case "Bronze": return <Award className={`${className} text-orange-500`} />;
    default: return <Users className={`${className} text-slate-400`} />;
  }
};

const TierBadge = ({ tier, size = "sm" }) => {
  const style = TIER_COLORS[tier] || TIER_COLORS.Unranked;
  return (
    <Badge className={cn("gap-1", style.bg, style.text, "border", style.border, size === "sm" && "text-xs")}>
      <TierIcon tier={tier} className="h-3 w-3" />
      {tier}
    </Badge>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    // Check if it's a valid date format (YYYY-MM-DD)
    const datePattern = /^\d{4}-\d{2}-\d{2}/;
    if (!datePattern.test(dateStr)) return "—";
    
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "—";
    
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tierBreakdown, setTierBreakdown] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState([]);
  const [therapeuticAreas, setTherapeuticAreas] = useState([]);
  const [followUpsDue, setFollowUpsDue] = useState([]);
  const [followUpsPastDue, setFollowUpsPastDue] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [outreachStats, setOutreachStats] = useState(null);
  const [opportunityStats, setOpportunityStats] = useState(null);
  const [recentOpportunities, setRecentOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [csmAlerts, setCsmAlerts] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState([]);
  const [feasibilityStats, setFeasibilityStats] = useState(null);
  const [pipelineAnalytics, setPipelineAnalytics] = useState(null);
  const [densityData, setDensityData] = useState(null);
  const [dailyDigest, setDailyDigest] = useState(null);
  const [newOpps, setNewOpps] = useState(null);
  const [sponsorIntel, setSponsorIntel] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [loadingReport, setLoadingReport] = useState(false);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, tierRes, statusRes, taRes, followUpsRes, pastDueRes, activityRes, outreachRes, oppStatsRes, oppRecentRes, csmRes, pipelineRes, feasRes, analyticsRes, densityRes, digestRes, newOppsRes, sponsorRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/tier-breakdown`),
        axios.get(`${API}/dashboard/status-breakdown`),
        axios.get(`${API}/dashboard/therapeutic-areas`),
        axios.get(`${API}/dashboard/follow-ups-due`),
        axios.get(`${API}/dashboard/follow-ups-past-due`),
        axios.get(`${API}/dashboard/recent-activity`),
        axios.get(`${API}/dashboard/outreach-stats`),
        axios.get(`${API}/opportunities/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/opportunities?limit=5&action_status=New`).catch(() => ({ data: [] })),
        axios.get(`${API}/dashboard/csm-alerts`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/pipeline-summary`).catch(() => ({ data: [] })),
        axios.get(`${API}/feasibilities/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/pipeline-analytics`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/follow-up-density`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/daily-digest`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/new-opportunities`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/sponsor-intelligence`).catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      setTierBreakdown(tierRes.data);
      setStatusBreakdown(statusRes.data.slice(0, 8));
      setTherapeuticAreas(taRes.data.slice(0, 8));
      setFollowUpsDue(followUpsRes.data);
      setFollowUpsPastDue(pastDueRes.data);
      setRecentActivity(activityRes.data);
      setOutreachStats(outreachRes.data);
      setOpportunityStats(oppStatsRes.data);
      setRecentOpportunities(oppRecentRes.data);
      setCsmAlerts(csmRes.data);
      setPipelineSummary(pipelineRes.data);
      setFeasibilityStats(feasRes.data);
      setPipelineAnalytics(analyticsRes.data);
      setDensityData(densityRes.data);
      setDailyDigest(digestRes.data);
      setNewOpps(newOppsRes.data);
      setSponsorIntel(sponsorRes.data);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch weekly report independently (updates on weekOffset change)
  const fetchWeeklyReport = async (offset) => {
    setLoadingReport(true);
    try {
      const res = await axios.get(`${API}/dashboard/weekly-report?week_offset=${offset}`);
      setWeeklyReport(res.data);
    } catch { setWeeklyReport(null); }
    finally { setLoadingReport(false); }
  };

  useEffect(() => { fetchWeeklyReport(weekOffset); }, [weekOffset]);

  const handleDismissFollowup = async (leadId, e) => {
    e.preventDefault();  // Prevent Link navigation
    e.stopPropagation();
    try {
      await axios.post(`${API}/leads/${leadId}/dismiss-followup`);
      setFollowUpsPastDue(prev => prev.filter(l => l.id !== leadId));
      toast.success("Lead dismissed from follow-up list");
    } catch (error) {
      console.error("Dismiss error:", error);
      toast.error("Failed to dismiss");
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/seed/initial-data`);
      toast.success(response.data.message);
      fetchDashboardData();
    } catch (error) {
      console.error("Seed error:", error);
      toast.error("Failed to seed data");
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2E7AD1]" />
      </div>
    );
  }

  const pieData = [
    { name: "Gold", value: stats?.gold_count || 0, color: TIER_COLORS.Gold.chart },
    { name: "Silver", value: stats?.silver_count || 0, color: TIER_COLORS.Silver.chart },
    { name: "Bronze", value: stats?.bronze_count || 0, color: TIER_COLORS.Bronze.chart },
    { name: "Unranked", value: stats?.unranked_count || 0, color: TIER_COLORS.Unranked.chart }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header with seed button */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1C2A25] font-['Outfit'] tracking-tight">Dashboard Overview</h1>
          <p className="text-slate-500 mt-1">Track your BD pipeline and tier priorities</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchDashboardData}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {stats?.total_leads === 0 && (
            <Button 
              onClick={handleSeedData} 
              disabled={seeding}
              className="bg-[#2D5A4C] hover:bg-[#23473C]"
              data-testid="seed-data-btn"
            >
              {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Load Initial Data
            </Button>
          )}
        </div>
      </div>

      {/* Tier Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { tier: "Gold", count: stats?.gold_count || 0, label: "Top Strategic Priorities", icon: Trophy, link: "/leads?tier=Gold" },
          { tier: "Silver", count: stats?.silver_count || 0, label: "High Priority", icon: Medal, link: "/leads?tier=Silver" },
          { tier: "Bronze", count: stats?.bronze_count || 0, label: "Active Monitoring", icon: Award, link: "/leads?tier=Bronze" },
          { tier: "Total", count: stats?.total_leads || 0, label: "Total Pipeline", icon: Users, link: "/leads" }
        ].map((item, idx) => (
          <Card 
            key={item.tier} 
            onClick={() => navigate(item.link)}
            className={`border-l-4 ${item.tier === "Gold" ? "border-l-[#D97757]" : item.tier === "Silver" ? "border-l-[#64748B]" : item.tier === "Bronze" ? "border-l-[#9A3412]" : "border-l-[#2D5A4C]"} animate-fade-in stagger-${idx + 1} cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all`}
            data-testid={`stat-card-${item.tier.toLowerCase()}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#71717A] uppercase tracking-wider font-['Outfit']">
                    {item.tier === "Total" ? "Total" : `${item.tier} Tier`}
                  </p>
                  <p className="text-3xl font-bold text-[#1C2A25] mt-1 font-['Outfit']">{item.count.toLocaleString()}</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">{item.label}</p>
                </div>
                <div className={`p-3 rounded-xl ${item.tier === "Total" ? "bg-[#2D5A4C]/10" : item.tier === "Gold" ? "bg-[#FEF0C7]" : item.tier === "Silver" ? "bg-[#F1F5F9]" : "bg-[#FFEDD5]"}`}>
                  <item.icon className={`h-6 w-6 ${item.tier === "Gold" ? "text-[#93370D]" : item.tier === "Silver" ? "text-[#475569]" : item.tier === "Bronze" ? "text-[#9A3412]" : "text-[#2D5A4C]"}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Status Breakdown - Moved to top */}
      <Card data-testid="status-breakdown">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Lead Status Distribution</CardTitle>
          <CardDescription>Pipeline engagement overview</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {statusBreakdown.map((status, idx) => {
              const percentage = stats?.total_leads ? Math.round((status.count / stats.total_leads) * 100) : 0;
              return (
                <div key={idx} className="space-y-2 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 truncate max-w-[150px]">
                      {status.status || "No Status"}
                    </span>
                    <span className="text-[#1C2A25] font-bold">{status.count}</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                  <p className="text-xs text-slate-400">{percentage}% of pipeline</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Outreach Tracker */}
      {outreachStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="stat-card" data-testid="outreach-weekly">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">This Week</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{outreachStats.this_week}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {outreachStats.week_change >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`text-xs font-medium ${outreachStats.week_change >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {outreachStats.week_change > 0 ? "+" : ""}{outreachStats.week_change}% vs last week
                    </span>
                  </div>
                </div>
                <div className="p-3 rounded-full bg-blue-100">
                  <Send className="h-6 w-6 text-[#2E7AD1]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="outreach-monthly">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">This Month</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{outreachStats.this_month}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {outreachStats.month_change >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`text-xs font-medium ${outreachStats.month_change >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {outreachStats.month_change > 0 ? "+" : ""}{outreachStats.month_change}% vs last month
                    </span>
                  </div>
                </div>
                <div className="p-3 rounded-full bg-green-100">
                  <CalendarDays className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="total-emails">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Emails Sent</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{outreachStats.total_emails_sent}</p>
                  <p className="text-xs text-slate-400 mt-1">All time outreach</p>
                </div>
                <div className="p-3 rounded-full bg-purple-100">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="response-rate">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Response Rate</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{outreachStats.response_rate}%</p>
                  <p className="text-xs text-slate-400 mt-1">{outreachStats.total_responses} responses</p>
                </div>
                <div className="p-3 rounded-full bg-orange-100">
                  <CheckCircle className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Digest + 5-Day Follow-Up Density */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Daily Digest Banner */}
        <Card className="lg:col-span-2" data-testid="daily-digest">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              Today's Digest
            </CardTitle>
            <CardDescription>
              {dailyDigest?.total_due_today || 0} follow-ups due today across all cadence profiles
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dailyDigest && dailyDigest.total_due_today > 0 ? (
              <div className="space-y-3">
                {Object.entries(dailyDigest.by_cadence).map(([cadenceName, leads]) => {
                  if (leads.length === 0) return null;
                  const profile = dailyDigest.cadence_profiles?.[cadenceName];
                  return (
                    <div key={cadenceName} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={cn("text-xs",
                            cadenceName === "Feasibility" ? "bg-amber-50 text-amber-700 border-amber-200" :
                            cadenceName === "Reactivation" ? "bg-teal-50 text-teal-700 border-teal-200" :
                            "bg-blue-50 text-blue-700 border-blue-200"
                          )}>
                            {cadenceName}
                          </Badge>
                          <span className="text-xs text-slate-400">Every {profile?.interval_days || "?"}d</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">{leads.length}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {leads.slice(0, 6).map((lead, idx) => (
                          <Link key={lead.id || idx} to={`/leads/${lead.id}`}>
                            <Badge variant="outline" className="text-xs hover:bg-slate-200 transition-colors cursor-pointer">
                              {lead.company || "?"} — {lead.contact || "N/A"}
                            </Badge>
                          </Link>
                        ))}
                        {leads.length > 6 && (
                          <Badge variant="secondary" className="text-xs">+{leads.length - 6} more</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center py-6 text-slate-400">
                <CheckCircle className="h-10 w-10 mb-2 text-green-400" />
                <p className="text-sm text-green-600">No follow-ups due today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 5-Day Density Strip */}
        <Card data-testid="follow-up-density">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              5-Day Load
            </CardTitle>
            <CardDescription>
              {densityData?.total_week || 0} follow-ups in next 5 business days
            </CardDescription>
          </CardHeader>
          <CardContent>
            {densityData && (
              <>
                <div className="flex gap-2 mb-4">
                  {densityData.density.map((day, idx) => (
                    <div key={idx} className="flex-1 text-center">
                      <p className="text-xs text-slate-500 mb-1">{day.day_name}</p>
                      <div className={cn(
                        "rounded-lg py-3 font-bold text-lg transition-colors",
                        day.level === "red" ? "bg-red-100 text-red-700" :
                        day.level === "amber" ? "bg-amber-100 text-amber-700" :
                        "bg-green-100 text-green-700"
                      )}>
                        {day.count}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">{day.date.slice(5)}</p>
                    </div>
                  ))}
                </div>
                {densityData.redistribution_needed && (
                  <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-700">
                      <span className="font-semibold">Overloaded!</span> {densityData.overloaded_days} day(s) have 7+ follow-ups. Consider redistributing to lighter days.
                    </p>
                  </div>
                )}
                <div className="flex justify-between mt-3 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-400" /> 1-3</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400" /> 4-6</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-400" /> 7+</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Opportunity Notifications */}
      {newOpps && newOpps.count > 0 && (
        <Card className="border-l-4 border-l-purple-400" data-testid="new-opp-notifications">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Bell className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">
                    {newOpps.count} new {newOpps.count === 1 ? "opportunity" : "opportunities"} discovered
                  </p>
                  <p className="text-xs text-slate-500">In the last 24 hours from automated searches</p>
                </div>
              </div>
              <Link to="/opportunities">
                <Button variant="outline" size="sm">
                  View <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            {newOpps.opportunities?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3 ml-12">
                {newOpps.opportunities.slice(0, 5).map((opp, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                    {opp.company_name} — {opp.indication || opp.event_type}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* BD Opportunities Widget */}
      {opportunityStats && (
        <Card data-testid="opportunities-widget">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Target className="h-5 w-5 text-purple-500" />
                BD Opportunities
              </CardTitle>
              <CardDescription>Clinical trial opportunities from ClinicalTrials.gov & AI search</CardDescription>
            </div>
            <Link to="/opportunities">
              <Button variant="outline" size="sm">
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <button
                type="button"
                onClick={() => navigate("/opportunities")}
                className="p-3 bg-purple-50 rounded-lg hover:bg-purple-100 hover:shadow-sm transition-all text-left cursor-pointer"
                data-testid="opp-stat-total"
              >
                <p className="text-2xl font-bold text-purple-700">{opportunityStats.total}</p>
                <p className="text-xs text-purple-600">Total Opportunities</p>
              </button>
              <button
                type="button"
                onClick={() => navigate("/opportunities?status=New")}
                className="p-3 bg-blue-50 rounded-lg hover:bg-blue-100 hover:shadow-sm transition-all text-left cursor-pointer"
                data-testid="opp-stat-new"
              >
                <p className="text-2xl font-bold text-blue-700">{opportunityStats.by_status?.New || 0}</p>
                <p className="text-xs text-blue-600">New</p>
              </button>
              <button
                type="button"
                onClick={() => navigate("/opportunities?in_crm=true")}
                className="p-3 bg-green-50 rounded-lg hover:bg-green-100 hover:shadow-sm transition-all text-left cursor-pointer"
                data-testid="opp-stat-crm"
              >
                <p className="text-2xl font-bold text-green-700">{opportunityStats.internal_company_count || 0}</p>
                <p className="text-xs text-green-600">CRM Companies</p>
              </button>
              <button
                type="button"
                onClick={() => navigate("/opportunities?recent_7_days=true")}
                className="p-3 bg-orange-50 rounded-lg hover:bg-orange-100 hover:shadow-sm transition-all text-left cursor-pointer"
                data-testid="opp-stat-recent"
              >
                <p className="text-2xl font-bold text-orange-700">{opportunityStats.recent_7_days || 0}</p>
                <p className="text-xs text-orange-600">Last 7 Days</p>
              </button>
            </div>
            
            {recentOpportunities.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Recent New Opportunities</p>
                <div className="space-y-2">
                  {recentOpportunities.slice(0, 5).map((opp, idx) => (
                    <Link 
                      key={opp.id || idx}
                      to="/opportunities"
                      className="block"
                    >
                      <div className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          {opp.source === "ClinicalTrials.gov" ? (
                            <Beaker className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          ) : (
                            <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{opp.company_name}</p>
                            <p className="text-xs text-slate-500 truncate">{opp.indication || opp.event_type}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs flex-shrink-0">
                          {opp.current_phase || "N/A"}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CSM Alerts + Pipeline Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Success Management Alerts */}
        {csmAlerts && csmAlerts.total_stale > 0 && (
          <Card data-testid="csm-alerts">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-amber-500" />
                  Customer Success Alerts
                </CardTitle>
                <CardDescription>Stale high-priority contacts (no update in 60+ days)</CardDescription>
              </div>
              <div className="flex gap-2">
                <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200">
                  <Trophy className="h-3 w-3 mr-1" /> {csmAlerts.gold_stale} Gold
                </Badge>
                <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
                  <Medal className="h-3 w-3 mr-1" /> {csmAlerts.silver_stale} Silver
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[280px]">
                <div className="space-y-2">
                  {csmAlerts.stale_leads.map((lead, idx) => (
                    <Link
                      key={lead.id || idx}
                      to={`/leads/${lead.id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-3 rounded-lg hover:bg-amber-50 border border-transparent hover:border-amber-200 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <TierBadge tier={lead.company_tier} />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{lead.company || "Unknown"}</p>
                            <p className="text-xs text-slate-500 truncate">{lead.contact || "N/A"}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600 border-amber-200">
                            <UserX className="h-3 w-3 mr-1" />
                            Stale
                          </Badge>
                          <p className="text-xs text-slate-400 mt-1">
                            {lead.date_latest_email_sent ? `Last email: ${formatDate(lead.date_latest_email_sent)}` : "No outreach"}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Pipeline Stage Summary */}
        {pipelineSummary.length > 0 && (
          <Card data-testid="pipeline-summary">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-500" />
                Pipeline Stages
              </CardTitle>
              <CardDescription>Contact distribution across pipeline</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pipelineSummary.map((item, idx) => {
                  const total = pipelineSummary.reduce((sum, i) => sum + i.count, 0);
                  const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-700">{item.stage}</span>
                        <span className="text-sm font-bold text-slate-900">{item.count}</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Feasibility Stats + Pipeline Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feasibility Stats */}
        {feasibilityStats && (
          <Card data-testid="feasibility-stats">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Beaker className="h-5 w-5 text-purple-500" />
                Feasibility Overview
              </CardTitle>
              <CardDescription>Submission tracking & win rate</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-xl font-bold text-amber-700">{feasibilityStats.pending}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-700">{feasibilityStats.selected}</p>
                  <p className="text-xs text-green-600">Won</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-700">{feasibilityStats.not_selected}</p>
                  <p className="text-xs text-red-600">Lost</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  <span className="text-slate-600">Win Rate:</span>
                  <span className="font-bold text-green-600">{feasibilityStats.win_rate}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span className="text-slate-600">Avg Decision:</span>
                  <span className="font-bold text-blue-600">{feasibilityStats.avg_days_to_decision} days</span>
                </div>
              </div>
              {feasibilityStats.win_reasons?.length > 0 && (
                <div className="mt-4 space-y-1">
                  <p className="text-xs text-slate-500 font-medium">Top Win Reasons</p>
                  {feasibilityStats.win_reasons.slice(0, 3).map((wr, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      <span className="text-slate-600">{wr.reason}</span>
                      <span className="font-bold text-green-700 ml-auto">{wr.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pipeline Velocity */}
        {pipelineAnalytics && (
          <Card data-testid="pipeline-analytics">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-500" />
                Pipeline Analytics
              </CardTitle>
              <CardDescription>Velocity metrics and stage flow</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl font-bold text-blue-700">{pipelineAnalytics.velocity?.moves_7d || 0}</p>
                  <p className="text-xs text-blue-600">Moves (7 days)</p>
                </div>
                <div className="text-center p-3 bg-indigo-50 rounded-lg">
                  <p className="text-xl font-bold text-indigo-700">{pipelineAnalytics.velocity?.moves_30d || 0}</p>
                  <p className="text-xs text-indigo-600">Moves (30 days)</p>
                </div>
              </div>
              {/* Funnel visualization */}
              {pipelineAnalytics.funnel && (
                <div className="space-y-1.5">
                  <p className="text-xs text-slate-500 font-medium mb-2">Stage Funnel</p>
                  {pipelineAnalytics.funnel.filter(s => s.count > 0).map((stage, idx) => {
                    const maxCount = Math.max(...pipelineAnalytics.funnel.map(s => s.count), 1);
                    const widthPct = Math.max((stage.count / maxCount) * 100, 8);
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-xs text-slate-600 w-28 truncate">{stage.stage}</span>
                        <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-end pr-2 text-white text-[10px] font-bold transition-all"
                            style={{ width: `${widthPct}%` }}
                          >
                            {stage.count}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Avg time in stage */}
              {pipelineAnalytics.avg_time_in_stage && Object.keys(pipelineAnalytics.avg_time_in_stage).length > 0 && (
                <div className="mt-4 space-y-1">
                  <p className="text-xs text-slate-500 font-medium">Avg Time in Stage</p>
                  {Object.entries(pipelineAnalytics.avg_time_in_stage).map(([stage, days], i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-slate-600">{stage}</span>
                      <Badge variant="outline" className={cn("text-xs", days > 30 ? "text-red-600 bg-red-50" : days > 14 ? "text-amber-600 bg-amber-50" : "text-green-600 bg-green-50")}>
                        {days} days
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sponsor Intelligence */}
      {sponsorIntel && (sponsorIntel.sponsors?.length > 0 || sponsorIntel.pending_by_sponsor?.length > 0) && (
        <Card data-testid="sponsor-intelligence">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-500" />
              Sponsor Intelligence
            </CardTitle>
            <CardDescription>Win/loss patterns and recommended focus sponsors</CardDescription>
          </CardHeader>
          <CardContent>
            {sponsorIntel.sponsors?.length > 0 && (
              <div className="space-y-3 mb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 text-xs text-slate-500 font-medium">Sponsor</th>
                        <th className="text-center py-2 text-xs text-slate-500 font-medium">Total</th>
                        <th className="text-center py-2 text-xs text-slate-500 font-medium">Won</th>
                        <th className="text-center py-2 text-xs text-slate-500 font-medium">Lost</th>
                        <th className="text-center py-2 text-xs text-slate-500 font-medium">Win Rate</th>
                        <th className="text-left py-2 text-xs text-slate-500 font-medium">Top Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sponsorIntel.sponsors.map((s, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2.5 font-medium text-slate-900">{s.sponsor}</td>
                          <td className="py-2.5 text-center text-slate-600">{s.total}</td>
                          <td className="py-2.5 text-center text-green-600 font-medium">{s.wins}</td>
                          <td className="py-2.5 text-center text-red-600 font-medium">{s.losses}</td>
                          <td className="py-2.5 text-center">
                            <Badge className={cn("text-xs border-0",
                              s.win_rate >= 60 ? "bg-green-100 text-green-700" :
                              s.win_rate >= 40 ? "bg-amber-100 text-amber-700" :
                              "bg-red-100 text-red-700"
                            )}>
                              {s.win_rate}%
                            </Badge>
                          </td>
                          <td className="py-2.5 text-xs text-slate-500">
                            {s.wins > s.losses ? (
                              <span className="text-green-600">{s.top_win_reason || "—"}</span>
                            ) : (
                              <span className="text-red-600">{s.top_loss_reason || "—"}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {sponsorIntel.pending_by_sponsor?.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 font-medium mb-2">Pending Decisions by Sponsor</p>
                <div className="flex flex-wrap gap-2">
                  {sponsorIntel.pending_by_sponsor.map((ps, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 gap-1">
                      {ps.sponsor}: {ps.pending} pending
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Weekly BD Performance Report */}
      <Card data-testid="weekly-report">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-emerald-500" />
              Weekly BD Performance Report
            </CardTitle>
            <CardDescription>
              {weeklyReport?.week_label || "Loading..."}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setWeekOffset(o => o + 1)} disabled={loadingReport} data-testid="report-prev-week">
              Prev Week
            </Button>
            {weekOffset > 0 && (
              <Button variant="outline" size="sm" onClick={() => setWeekOffset(o => Math.max(0, o - 1))} disabled={loadingReport} data-testid="report-next-week">
                Next Week
              </Button>
            )}
            {weekOffset > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)} data-testid="report-current-week">
                Current
              </Button>
            )}
            {loadingReport && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
        </CardHeader>
        {weeklyReport && (
          <CardContent>
            {/* Key Metrics Row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-700">{weeklyReport.pipeline_activity.total_stage_moves}</p>
                <p className="text-xs text-blue-600">Pipeline Moves</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-700">{weeklyReport.pipeline_activity.unique_contacts_moved}</p>
                <p className="text-xs text-purple-600">Contacts Moved</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-700">{weeklyReport.feasibilities.submitted}</p>
                <p className="text-xs text-amber-600">Feasibilities Sent</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{weeklyReport.feasibilities.won}</p>
                <p className="text-xs text-green-600">Wins</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-700">{weeklyReport.feasibilities.lost}</p>
                <p className="text-xs text-red-600">Losses</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-indigo-700">{weeklyReport.opportunities.new_discovered}</p>
                <p className="text-xs text-indigo-600">New Opps</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Top BD Members */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <Trophy className="h-4 w-4 text-amber-500" /> Top Performers
                </p>
                {weeklyReport.top_bd_members?.length > 0 ? (
                  <div className="space-y-2">
                    {weeklyReport.top_bd_members.slice(0, 5).map((bd, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold",
                            idx === 0 ? "bg-amber-100 text-amber-700" :
                            idx === 1 ? "bg-slate-200 text-slate-600" :
                            "bg-orange-100 text-orange-600"
                          )}>
                            {idx + 1}
                          </span>
                          <span className="text-sm text-slate-800">{bd.bd_member || "Unknown"}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-slate-900">{bd.moves}</span>
                          <span className="text-xs text-slate-400 ml-1">moves</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 py-4 text-center">No pipeline activity this week</p>
                )}
              </div>

              {/* Tier Activity */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <Target className="h-4 w-4 text-blue-500" /> Activity by Tier
                </p>
                {weeklyReport.tier_activity?.length > 0 ? (
                  <div className="space-y-2">
                    {weeklyReport.tier_activity.map((ta, idx) => {
                      const maxMoves = Math.max(...weeklyReport.tier_activity.map(t => t.moves), 1);
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-700">{ta.tier}</span>
                            <span className="text-sm font-bold">{ta.moves}</span>
                          </div>
                          <Progress value={(ta.moves / maxMoves) * 100} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 py-4 text-center">No tier activity this week</p>
                )}

                {/* Win Rate */}
                {weeklyReport.feasibilities.decided > 0 && (
                  <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-emerald-700">Weekly Win Rate</span>
                      <span className="text-lg font-bold text-emerald-700">{weeklyReport.feasibilities.win_rate}%</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">
                      {weeklyReport.feasibilities.won}W / {weeklyReport.feasibilities.lost}L of {weeklyReport.feasibilities.decided} decided
                    </p>
                  </div>
                )}
              </div>

              {/* Monday Priority Follow-ups */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4 text-red-500" /> Monday Priority Follow-ups
                </p>
                {weeklyReport.monday_priority_followups?.length > 0 ? (
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-1.5">
                      {weeklyReport.monday_priority_followups.map((fu, idx) => (
                        <Link key={fu.id || idx} to={`/leads/${fu.id}`}>
                          <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{fu.company || "?"}</p>
                              <p className="text-xs text-slate-500 truncate">{fu.contact || "N/A"}</p>
                            </div>
                            <TierBadge tier={fu.company_tier} />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="flex flex-col items-center py-6 text-slate-400">
                    <CheckCircle className="h-8 w-8 mb-1 text-green-400" />
                    <p className="text-xs text-green-600">No high-priority follow-ups for Monday</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Moves */}
            {weeklyReport.pipeline_activity.recent_moves?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs font-medium text-slate-500 mb-2">Recent Pipeline Moves</p>
                <div className="flex flex-wrap gap-1.5">
                  {weeklyReport.pipeline_activity.recent_moves.slice(0, 8).map((move, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs gap-1">
                      {move.lead_company || "?"}: {move.from} <ArrowRight className="h-2.5 w-2.5" /> {move.to}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Follow-ups Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Follow-ups Due This Week */}
        <Card data-testid="follow-ups-due">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Follow-ups Due This Week
              </CardTitle>
              <CardDescription>{followUpsDue.length} leads need attention</CardDescription>
            </div>
            <Badge className="bg-blue-100 text-blue-700">{followUpsDue.length}</Badge>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              {followUpsDue.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <CheckCircle className="h-12 w-12 mb-2" />
                  <p>No follow-ups due this week</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {followUpsDue.map((lead, idx) => (
                    <Link 
                      key={lead.id || idx} 
                      to={`/leads/${lead.id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <TierBadge tier={lead.company_tier} />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{lead.company || "Unknown"}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {lead.first_name || lead.contact || "N/A"} {lead.last_name || ""}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">
                            FU #{lead.follow_up_number}
                          </Badge>
                          <p className="text-xs text-slate-500 mt-1">{formatDate(lead.follow_up_due_date)}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Past Due Follow-ups */}
        <Card data-testid="follow-ups-past-due">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Past Due Follow-ups
              </CardTitle>
              <CardDescription>Requires immediate attention. Click ✕ to dismiss.</CardDescription>
            </div>
            <Badge className="bg-red-100 text-red-700">{followUpsPastDue.length}</Badge>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              {followUpsPastDue.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <CheckCircle className="h-12 w-12 mb-2 text-green-500" />
                  <p className="text-green-600">All caught up!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {followUpsPastDue.map((lead, idx) => (
                    <Link 
                      key={lead.id || idx} 
                      to={`/leads/${lead.id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-3 rounded-lg hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors bg-red-50/50">
                        <div className="flex items-center gap-3 min-w-0">
                          <TierBadge tier={lead.company_tier} />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{lead.company || "Unknown"}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {lead.first_name || lead.contact || "N/A"} {lead.last_name || ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <div className="text-right">
                            <Badge variant="outline" className="text-xs bg-red-100 text-red-600 border-red-200">
                              {lead.days_overdue}d overdue
                            </Badge>
                            <p className="text-xs text-slate-500 mt-1">FU #{lead.follow_up_number}</p>
                          </div>
                          <button
                            onClick={(e) => handleDismissFollowup(lead.id, e)}
                            className="p-1.5 rounded-full hover:bg-red-200 text-red-400 hover:text-red-600 transition-colors"
                            title="Dismiss from list"
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tier Distribution Pie Chart */}
        <Card className="lg:col-span-1" data-testid="tier-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Tier Distribution</CardTitle>
            <CardDescription>Leads by strategic tier</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [value, `${name} Tier`]}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Therapeutic Areas Bar Chart */}
        <Card className="lg:col-span-2" data-testid="ta-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Top Therapeutic Areas</CardTitle>
            <CardDescription>Lead distribution by specialty</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={therapeuticAreas} layout="vertical" margin={{ left: 80 }}>
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="area" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                  <Bar dataKey="count" fill="#2E7AD1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Companies by Tier */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Email Activity */}
        <Card data-testid="recent-activity">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Mail className="h-5 w-5 text-[#2E7AD1]" />
                Recent Email Activity
              </CardTitle>
              <CardDescription>Latest outreach by company</CardDescription>
            </div>
            <Link to="/leads">
              <Button variant="outline" size="sm">
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              <div className="space-y-3">
                {recentActivity.slice(0, 15).map((lead, idx) => (
                  <Link 
                    key={lead.id || idx} 
                    to={`/leads/${lead.id}`}
                    className="block"
                  >
                    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-blue-50 rounded-full">
                          <Send className="h-4 w-4 text-[#2E7AD1]" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{lead.company || "Unknown"}</p>
                          <p className="text-xs text-slate-500">
                            {lead.first_name || lead.contact || "N/A"} • {lead.email?.slice(0, 25) || "No email"}...
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <TierBadge tier={lead.company_tier} />
                        <p className="text-xs text-slate-400 mt-1">{formatDate(lead.date_first_email_sent)}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Companies by Tier */}
        <Card data-testid="tier-breakdown">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold">Companies by Tier</CardTitle>
              <CardDescription>Quick access to tier leads</CardDescription>
            </div>
            <Link to="/leads">
              <Button variant="outline" size="sm">
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {tierBreakdown.filter(t => t.tier !== "Unranked").map((tier) => (
                <Link 
                  key={tier.tier} 
                  to={`/leads?tier=${tier.tier}`}
                  className="block"
                >
                  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200">
                    <div className="flex items-center gap-3">
                      <TierIcon tier={tier.tier} />
                      <div>
                        <p className="font-medium text-slate-900">{tier.tier} Tier</p>
                        <p className="text-sm text-slate-500">
                          {tier.company_count} companies • {tier.total_leads} leads
                        </p>
                      </div>
                    </div>
                    <Badge className={`${TIER_COLORS[tier.tier]?.bg} ${TIER_COLORS[tier.tier]?.text} border ${TIER_COLORS[tier.tier]?.border}`}>
                      {tier.total_leads}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>

            {/* Weekly Outreach by Tier */}
            {outreachStats && (
              <div className="mt-6 pt-4 border-t">
                <p className="text-sm font-medium text-slate-700 mb-3">This Week's Outreach by Tier</p>
                <div className="grid grid-cols-4 gap-2">
                  {outreachStats.tier_outreach_this_week?.map((item) => (
                    <div key={item.tier} className="text-center p-2 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold text-slate-900">{item.count}</p>
                      <p className="text-xs text-slate-500">{item.tier}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
